import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  Calculate, ContentCut, ShoppingBag, People,
  Receipt, History, Business, Assessment, Settings
} from '@mui/icons-material';
import './Dashboard.css';

const Dashboard = () => {
  const { user } = useAuth();

  const [businessStats, setBusinessStats] = useState({
    serviceCount: 0,
    productCount: 0,
    staffCount: 0,
    totalOverhead: 0,
    totalStaffCost: 0,
    tradingDaysPerWeek: 5,
    tradingHoursPerWeek: 40,
    hoursPerDay: 8
  });

  const [recentCalculations, setRecentCalculations] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [error, setError]         = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const axiosConfig = {
        timeout: 10000,
        headers: token && token.includes('.')
          ? { Authorization: `Bearer ${token}` }
          : {}
      };

      const [
        servicesRes, productsRes, staffRes, overheadRes,
        hourlyCostRes, tradingHoursRes, businessSettingsRes
      ] = await Promise.all([
        axios.get('/api/services',              axiosConfig).catch(() => ({ data: [] })),
        axios.get('/api/products',              axiosConfig).catch(() => ({ data: [] })),
        axios.get('/api/staff',                 axiosConfig).catch(() => ({ data: [] })),
        axios.get('/api/overhead',              axiosConfig).catch(() => ({ data: [] })),
        axios.get('/api/calculator/hourly-cost',axiosConfig).catch(() => ({ data: {} })),
        axios.get('/api/trading-hours/',        axiosConfig).catch(() => ({ data: {} })),
        axios.get('/api/business/settings',     axiosConfig).catch(() => ({ data: {} }))
      ]);

      const services         = Array.isArray(servicesRes.data)  ? servicesRes.data  : [];
      const products         = Array.isArray(productsRes.data)  ? productsRes.data  : [];
      const staff            = Array.isArray(staffRes.data)     ? staffRes.data     : [];
      const overhead         = Array.isArray(overheadRes.data)  ? overheadRes.data  : [];
      const hourlyCostData   = hourlyCostRes.data        || {};
      const tradingHoursData = tradingHoursRes.data      || {};
      const businessSettings = businessSettingsRes.data  || {};

      // ── Trading hours ───────────────────────────────────────────────────────
      let totalWorkingDays   = 0;
      let weeklyWorkingHours = 0;

      if (tradingHoursData?.trading_hours) {
        Object.values(tradingHoursData.trading_hours).forEach(day => {
          if (day?.open === true && day.start && day.end) {
            totalWorkingDays++;
            try {
              const [sh, sm] = day.start.split(':').map(Number);
              const [eh, em] = day.end.split(':').map(Number);
              const hrs = (eh + em / 60) - (sh + sm / 60);
              if (hrs > 0 && hrs <= 24) weeklyWorkingHours += hrs;
            } catch (_) {}
          }
        });
      }

      if (!totalWorkingDays)   totalWorkingDays   = parseInt(businessSettings.total_working_days)      || 5;
      if (!weeklyWorkingHours) weeklyWorkingHours = parseFloat(businessSettings.weekly_working_hours)  || 40;

      const hoursPerDay = totalWorkingDays > 0 ? weeklyWorkingHours / totalWorkingDays : 8;

      // ── Staff cost ──────────────────────────────────────────────────────────
      let calculatedStaffCost = 0;
      staff.forEach(member => {
        const hourlyPay   = parseFloat(member.hourly_pay       || 0);
        const weeklyHours = parseFloat(member.available_hours  || 0);
        const pension     = parseFloat(member.pension_rate     || 3.0) / 100;
        const ni          = parseFloat(member.ni_rate          || 15)  / 100;
        const monthly     = hourlyPay * weeklyHours * (52 / 12);
        calculatedStaffCost += monthly * (1 + pension + ni);
      });

      // ── Overhead cost ───────────────────────────────────────────────────────
      let calculatedOverhead = 0;
      if (hourlyCostData.total_overheads) {
        calculatedOverhead = hourlyCostData.total_overheads;
      } else {
        const multipliers = { annually: 1/12, annual: 1/12, quarterly: 1/3, weekly: 4.33 };
        overhead.forEach(item => {
          const amount = parseFloat(item.amount || 0);
          const m      = multipliers[(item.regularity || '').toLowerCase()] ?? 1;
          calculatedOverhead += amount * m;
        });
      }

      setBusinessStats({
        serviceCount:        services.length,
        productCount:        products.length,
        staffCount:          staff.length,
        totalOverhead:       calculatedOverhead,
        totalStaffCost:      calculatedStaffCost,
        tradingDaysPerWeek:  totalWorkingDays,
        tradingHoursPerWeek: weeklyWorkingHours,
        hoursPerDay
      });

      setRecentCalculations([]);
    } catch (_) {
      setError('Unable to load some dashboard data. The calculator functions are still available.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const fmt = (v) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(v || 0);

  const totalMonthly  = businessStats.totalStaffCost + businessStats.totalOverhead;
  const staffPct      = totalMonthly > 0 ? Math.round((businessStats.totalStaffCost / totalMonthly) * 100) : 0;
  const overheadPct   = totalMonthly > 0 ? Math.round((businessStats.totalOverhead  / totalMonthly) * 100) : 0;
  const dailyBreakeven  = totalMonthly / (businessStats.tradingDaysPerWeek * 4.33);
  const hourlyBreakeven = dailyBreakeven / businessStats.hoursPerDay;

  const quickLinks = [
    { title: 'New Calculation',   icon: <Calculate />,   path: '/calculator',          primary: true },
    { title: 'Manage Services',   icon: <ContentCut />,  path: '/calculator/services' },
    { title: 'Manage Products',   icon: <ShoppingBag />, path: '/calculator/products' },
    { title: 'Staff Management',  icon: <People />,      path: '/calculator/staff' },
    { title: 'Business Settings', icon: <Settings />,    path: '/calculator/settings' },
    { title: 'Expenses',          icon: <Receipt />,     path: '/calculator/overheads' },
  ];

  // ── Loading state ────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  // ── Main render ──────────────────────────────────────────────────────────────
  return (
    <div className="dashboard-container">

      {error && (
        <div className="dashboard-alert">
          {error}
        </div>
      )}

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="dashboard-header">
        <h1>Hair &amp; Beauty Calculator Dashboard</h1>
        <p className="welcome-text">
          Welcome back, {user?.first_name || user?.name || user?.email || 'User'}! Manage your salon business with professional pricing insights.
        </p>

        <div className="dashboard-tabs">
          <button
            className={`tab-button ${activeTab === 0 ? 'active' : ''}`}
            onClick={() => setActiveTab(0)}
          >
            Business Overview
          </button>
          <button
            className={`tab-button ${activeTab === 1 ? 'active' : ''}`}
            onClick={() => setActiveTab(1)}
          >
            Recent Calculations
          </button>
        </div>
      </div>

      {/* ── Business Overview ───────────────────────────────────────────────── */}
      {activeTab === 0 && (
        <>
          {/* Metric cards */}
          <div className="metrics-row">
            <div className="metric-card">
              <div className="metric-icon metric-icon--amber"><ContentCut /></div>
              <div className="metric-title">Services</div>
              <div className="metric-value">{businessStats.serviceCount}</div>
              <div className="metric-footer">
                <Link to="/calculator/services">Manage Services</Link>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon metric-icon--green"><ShoppingBag /></div>
              <div className="metric-title">Products</div>
              <div className="metric-value">{businessStats.productCount}</div>
              <div className="metric-footer">
                <Link to="/calculator/products">Manage Products</Link>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon metric-icon--indigo"><People /></div>
              <div className="metric-title">Staff Members</div>
              <div className="metric-value">{businessStats.staffCount}</div>
              <div className="metric-footer">
                <Link to="/calculator/staff">Manage Staff</Link>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon metric-icon--red"><Receipt /></div>
              <div className="metric-title">Monthly Expenses</div>
              <div className="metric-value">{fmt(totalMonthly)}</div>
              <div className="metric-footer">
                <Link to="/calculator/overheads">View Breakdown</Link>
              </div>
            </div>
          </div>

          {/* Middle row */}
          <div className="dashboard-row">

            {/* Daily targets */}
            <div className="dashboard-card">
              <div className="card-header">
                <h2><Business style={{ verticalAlign: 'middle', marginRight: 8, fontSize: '1.1em' }} />Daily Business Targets</h2>
                <button className="btn-secondary btn-sm" onClick={fetchData} disabled={loading}>
                  {loading ? 'Refreshing…' : 'Refresh'}
                </button>
              </div>
              <div className="card-body">
                <div className="expense-item">
                  <span className="expense-label">Daily Breakeven ({businessStats.tradingDaysPerWeek} days/week)</span>
                  <span className="expense-value">
                    {fmt(dailyBreakeven)}
                    <span className="expense-badge expense-badge--blue">Per Day</span>
                  </span>
                </div>
                <div className="expense-item">
                  <span className="expense-label">Hourly Breakeven Rate</span>
                  <span className="expense-value">
                    {fmt(hourlyBreakeven)}
                    <span className="expense-badge expense-badge--amber">Per Hour</span>
                  </span>
                </div>
                <div className="target-summary">
                  <div className="target-summary__row">
                    <span className="target-summary__label">Monthly Target to Break Even</span>
                    <span className="target-summary__value">{fmt(totalMonthly)}</span>
                  </div>
                  <p className="target-summary__note">This is what you need to generate just to cover fixed costs</p>
                </div>
              </div>
            </div>

            {/* Expenses breakdown */}
            <div className="dashboard-card">
              <div className="card-header">
                <h2><Assessment style={{ verticalAlign: 'middle', marginRight: 8, fontSize: '1.1em' }} />Business Expenses Breakdown</h2>
              </div>
              <div className="card-body">
                <div className="expense-item">
                  <span className="expense-label">Staff Costs (inc. employer costs)</span>
                  <span className="expense-value">
                    {fmt(businessStats.totalStaffCost)}
                    <span className="expense-percentage">{staffPct}%</span>
                  </span>
                </div>
                <div className="expense-item">
                  <span className="expense-label">Overhead Costs</span>
                  <span className="expense-value">
                    {fmt(businessStats.totalOverhead)}
                    <span className="expense-percentage">{overheadPct}%</span>
                  </span>
                </div>
                <div className="target-summary">
                  <div className="target-summary__row">
                    <span className="target-summary__label">Total Monthly Expenses</span>
                    <span className="target-summary__value">{fmt(totalMonthly)}</span>
                  </div>
                  <p className="target-summary__note">This is automatically calculated from your staff and overhead data</p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick actions */}
          <div className="dashboard-card full-width">
            <div className="card-header">
              <h2><Assessment style={{ verticalAlign: 'middle', marginRight: 8, fontSize: '1.1em' }} />Quick Actions</h2>
            </div>
            <div className="card-body">
              <div className="quick-links">
                {quickLinks.map((link, i) => (
                  <Link
                    key={i}
                    to={link.path}
                    className={`quick-link-button ${link.primary ? 'primary' : ''}`}
                  >
                    <span className="quick-link-icon">{link.icon}</span>
                    {link.title}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Recent Calculations tab ─────────────────────────────────────────── */}
      {activeTab === 1 && (
        <div className="dashboard-card">
          <div className="card-header">
            <h2><History style={{ verticalAlign: 'middle', marginRight: 8, fontSize: '1.1em' }} />Recent Calculations</h2>
          </div>
          <div className="card-body">
            {recentCalculations.length > 0 ? (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date</th><th>Service</th><th>Total Cost</th>
                    <th>Recommended Price</th><th>Profit</th><th>Margin</th>
                  </tr>
                </thead>
                <tbody>
                  {recentCalculations.map((calc, i) => (
                    <tr key={calc.id || i}>
                      <td>{new Date(calc.date).toLocaleDateString('en-GB')}</td>
                      <td>{calc.name}</td>
                      <td>{fmt(calc.totalCost)}</td>
                      <td className="total-cell">{fmt(calc.recommendedPrice)}</td>
                      <td style={{ color: calc.profit > 0 ? '#059669' : '#dc2626', fontWeight: 600 }}>
                        {fmt(calc.profit)}
                      </td>
                      <td>
                        <span className={`margin-badge ${calc.margin > 50 ? 'margin-badge--green' : calc.margin > 25 ? 'margin-badge--amber' : 'margin-badge--red'}`}>
                          {calc.margin}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="no-data-message">
                <div className="no-data-icon"><History style={{ fontSize: '3rem', color: '#cbd5e1' }} /></div>
                <h3>Calculation History Coming Soon</h3>
                <p>This feature will track your pricing calculations and show profit optimisation insights.</p>
                <Link to="/calculator" className="quick-link-button primary" style={{ display: 'inline-flex', marginTop: 16 }}>
                  <Calculate style={{ marginRight: 8 }} /> Use Pricing Calculator
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;